import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { driverRecordShape } from './driverRecordShape';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';

const StyledDiv = styledComponent('div', {
  '& .hs-grid': {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 1fr) 2fr minmax(180px, 1fr)',
    gap: theme.spacing.sectionGap,
    alignItems: 'stretch',
    padding: '20px 24px',
    background: theme.colors.bgCard,
    border: theme.borders.card,
    borderRadius: theme.radii.card,
    boxShadow: theme.shadows.card,
  },

  '& .hs-stat': {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    justifyContent: 'center',
  },
  '& .hs-stat-end': {
    alignItems: 'flex-end',
    textAlign: 'right',
  },
  '& .hs-stat-value': {
    fontSize: theme.sizes.heroStatFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .hs-stat-value .hs-of': {
    color: theme.colors.textHint,
    fontWeight: theme.fontWeights.normal,
    margin: '0 2px',
  },
  '& .hs-stat-value .hs-denom': {
    color: theme.colors.textMuted,
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.sizes.statFontSize,
  },
  '& .hs-stat-value.hs-positive': {
    color: theme.colors.statusOnTrack,
  },
  '& .hs-stat-value.hs-caution': {
    color: theme.colors.statusBehind,
  },
  '& .hs-stat-value.hs-negative': {
    color: theme.colors.statusOffTrack,
  },
  '& .hs-stat-label': {
    fontSize: theme.sizes.captionFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginTop: '6px',
  },
  '& .hs-stat-sublabel': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    marginTop: '4px',
  },
  '& .hs-late': {
    color: theme.colors.statusOffTrack,
    fontWeight: theme.fontWeights.medium,
  },

  '& .hs-progress': {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    justifyContent: 'center',
    padding: '0 4px',
    borderLeft: `1px solid ${theme.colors.borderLight}`,
    borderRight: `1px solid ${theme.colors.borderLight}`,
  },
  '& .hs-progress-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  '& .hs-progress-title': {
    fontSize: theme.sizes.captionFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },
  '& .hs-progress-pct': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textBody,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: theme.fontWeights.medium,
  },
  '& .hs-progress-pct .hs-progress-pct-meta': {
    color: theme.colors.textMuted,
    fontWeight: theme.fontWeights.normal,
  },

  '& .hs-bar': {
    height: '10px',
    borderRadius: theme.radii.round,
    background: theme.colors.bgMuted,
    display: 'flex',
    overflow: 'hidden',
    border: `1px solid ${theme.colors.borderLight}`,
  },
  '& .hs-bar-seg': {
    height: '100%',
    transition: theme.transitions.default,
  },
  '& .hs-bar-seg-on-time': {
    background: `linear-gradient(90deg, ${theme.colors.statusOnTrack} 0%, ${theme.colors.statusOnTrackSoft} 100%)`,
  },
  '& .hs-bar-seg-behind': { background: theme.colors.statusBehindSoft },
  '& .hs-bar-seg-off-track': { background: theme.colors.statusOffTrackSoft },

  '& .hs-legend': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '14px',
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
  },
  '& .hs-legend-item': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontVariantNumeric: 'tabular-nums',
  },
  '& .hs-legend-dot': {
    width: '8px',
    height: '8px',
    borderRadius: theme.radii.circle,
    flexShrink: 0,
  },
  '& .hs-legend-dot-on-time': { background: theme.colors.statusOnTrack },
  '& .hs-legend-dot-behind': { background: theme.colors.statusBehindSoft },
  '& .hs-legend-dot-off-track': { background: theme.colors.statusOffTrackSoft },
  '& .hs-legend-dot-pending': { background: theme.colors.statusPending },
});

function pct(part, whole) {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}

// On-time-rate color thresholds. PLACEHOLDER values pending team sign-off — the
// rerouting review floated a 95%+ green target (the old code painted any rate
// green, so 57% read as "fine"). Until the threshold + on-time definition are
// confirmed, gate the color: green at/above target, amber in the warn band, red
// below. Tune these two constants once the team locks the numbers.
const ON_TIME_TARGET_PCT = 95;
const ON_TIME_WARN_PCT = 80;

function onTimeRateTone(rate) {
  if (rate === null) return '';
  if (rate >= ON_TIME_TARGET_PCT) return 'hs-positive';
  if (rate >= ON_TIME_WARN_PCT) return 'hs-caution';
  return 'hs-negative';
}

function HeroSummary({ drivers }) {
  const summary = useMemo(() => {
    const byStatus = {
      'on-track': 0,
      behind: 0,
      'off-track': 0,
      'not-started': 0,
      completed: 0,
    };
    let totalStops = 0;
    let stocked = 0;
    let onTimeStops = 0;
    let behindStops = 0;
    let offTrackStops = 0;

    drivers.forEach((d) => {
      if (byStatus[d.status] !== undefined) byStatus[d.status] += 1;
      totalStops += d.stopsTotal || 0;
      stocked += d.stopsCompleted || 0;
      // Attribute completed stops to the driver's current tier — keeps the
      // bar in sync with the per-driver chips and card colors.
      const done = d.stopsCompleted || 0;
      if (d.status === 'off-track') offTrackStops += done;
      else if (d.status === 'behind') behindStops += done;
      else onTimeStops += done; // on-track | completed | not-started
    });

    const totalRoutes = drivers.length;
    const activeCount =
      byStatus['on-track'] + byStatus.behind + byStatus['off-track'];
    const lateCount = byStatus.behind + byStatus['off-track'];

    // Of currently-running routes, share that are on-track. Excludes
    // completed and not-started so the rate reflects "right now" health.
    const runningCount = byStatus['on-track'] + lateCount;
    const onTimeRate =
      runningCount === 0 ? null : pct(byStatus['on-track'], runningCount);

    return {
      totalRoutes,
      activeCount,
      notStartedCount: byStatus['not-started'],
      completedCount: byStatus.completed,
      lateCount,
      onTrackCount: byStatus['on-track'],
      totalStops,
      stocked,
      onTimeStops,
      behindStops,
      offTrackStops,
      onTimeRate,
    };
  }, [drivers]);

  const onTimePct = pct(summary.onTimeStops, summary.totalStops);
  const behindPct = pct(summary.behindStops, summary.totalStops);
  const offTrackPct = pct(summary.offTrackStops, summary.totalStops);
  const pendingPct = Math.max(0, 100 - onTimePct - behindPct - offTrackPct);
  const stockedPct = pct(summary.stocked, summary.totalStops);

  return (
    <StyledDiv>
      <div className="hs-grid">
        <div className="hs-stat">
          <span className="hs-stat-value">
            {summary.activeCount}
            <span className="hs-of">/</span>
            <span className="hs-denom">{summary.totalRoutes}</span>
          </span>
          <span className="hs-stat-label">Active routes</span>
          <span className="hs-stat-sublabel">
            {summary.notStartedCount} not yet started · {summary.completedCount}{' '}
            completed
          </span>
        </div>

        <div className="hs-progress">
          <div className="hs-progress-header">
            <span className="hs-progress-title">Today&apos;s progress</span>
            <span className="hs-progress-pct">
              {summary.stocked} / {summary.totalStops} stops{' '}
              <span className="hs-progress-pct-meta">· {stockedPct}%</span>
            </span>
          </div>
          <div
            className="hs-bar"
            role="progressbar"
            aria-valuenow={stockedPct}
            aria-valuemin="0"
            aria-valuemax="100"
            aria-label="Stops delivered"
          >
            {onTimePct > 0 && (
              <div
                className="hs-bar-seg hs-bar-seg-on-time"
                style={{ width: `${onTimePct}%` }}
              />
            )}
            {behindPct > 0 && (
              <div
                className="hs-bar-seg hs-bar-seg-behind"
                style={{ width: `${behindPct}%` }}
              />
            )}
            {offTrackPct > 0 && (
              <div
                className="hs-bar-seg hs-bar-seg-off-track"
                style={{ width: `${offTrackPct}%` }}
              />
            )}
          </div>
          <div className="hs-legend">
            <span className="hs-legend-item">
              <span className="hs-legend-dot hs-legend-dot-on-time" />
              {onTimePct}% on-time
            </span>
            <span className="hs-legend-item">
              <span className="hs-legend-dot hs-legend-dot-behind" />
              {behindPct}% behind
            </span>
            <span className="hs-legend-item">
              <span className="hs-legend-dot hs-legend-dot-off-track" />
              {offTrackPct}% off-track
            </span>
            <span className="hs-legend-item">
              <span className="hs-legend-dot hs-legend-dot-pending" />
              {pendingPct}% pending
            </span>
          </div>
        </div>

        <div className="hs-stat hs-stat-end">
          <span
            className={`hs-stat-value ${onTimeRateTone(summary.onTimeRate)}`}
          >
            {summary.onTimeRate === null ? '—' : summary.onTimeRate}
            {summary.onTimeRate !== null && <span className="hs-of">%</span>}
          </span>
          <span className="hs-stat-label">On-time rate</span>
          <span className="hs-stat-sublabel">
            {summary.lateCount > 0 && (
              <span className="hs-late">{summary.lateCount} late</span>
            )}
            {summary.lateCount > 0 && summary.onTrackCount > 0 && ' · '}
            {summary.onTrackCount > 0 && `${summary.onTrackCount} on-track`}
            {summary.lateCount === 0 &&
              summary.onTrackCount === 0 &&
              'No active routes'}
          </span>
        </div>
      </div>
    </StyledDiv>
  );
}

HeroSummary.propTypes = {
  drivers: PropTypes.arrayOf(driverRecordShape).isRequired,
};

export default HeroSummary;
